import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { taskPhotoService } from '../../../services/taskPhotoService';

const PhotoDetailModal = ({ photo, onClose, onDelete, canDelete, canManage, onToggleClientVisible }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!photo?.filePath) return;
    const load = async () => {
      setLoading(true);
      const { url, error: err } = await taskPhotoService?.getSignedUrl(photo?.filePath, 3600);
      if (err) {
        console.error('[PhotoDetailModal] signed URL failed:', { photoId: photo?.id, code: err?.code, message: err?.message });
      }
      setImgUrl(url || null);
      setLoading(false);
    };
    load();
  }, [photo?.filePath]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    setDeleting(true);
    await onDelete?.(photo?.id, photo?.filePath);
    setDeleting(false);
    onClose?.();
  };

  const formatDate = (iso) => {
    if (!iso) return 'Unknown';
    return new Date(iso)?.toLocaleString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground truncate">{photo?.task?.title || 'Site Photo'}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{photo?.site?.site_name || 'Unknown Site'}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canManage && (
              <button
                onClick={() => onToggleClientVisible?.(photo?.id, !photo?.clientVisible)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  photo?.clientVisible
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                }`}
                title={photo?.clientVisible ? 'Visible to client — click to hide' : 'Hidden from client — click to show'}
              >
                <Icon name={photo?.clientVisible ? 'Eye' : 'EyeOff'} size={13} />
                {photo?.clientVisible ? 'Visible to client' : 'Hidden from client'}
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors disabled:opacity-60"
              >
                {deleting ? <div className="w-3 h-3 border border-red-400 border-t-red-700 rounded-full animate-spin" /> : <Icon name="Trash2" size={13} />}
                Delete
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Image */}
        <div
          className={`relative flex-1 overflow-hidden bg-black flex items-center justify-center cursor-zoom-in min-h-0 ${
            zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          onClick={() => setZoomed(!zoomed)}
        >
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              <span className="text-white/60 text-sm">Loading...</span>
            </div>
          )}
          {imgUrl && (
            <img
              src={imgUrl}
              alt={`Task photo: ${photo?.task?.title || 'task documentation'}`}
              className={`max-h-full transition-transform duration-300 ${
                zoomed ? 'scale-150 max-w-none' : 'max-w-full object-contain'
              }`}
            />
          )}
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg">
            {zoomed ? 'Click to zoom out' : 'Click to zoom in'}
          </div>
        </div>

        {/* Metadata */}
        <div className="px-5 py-4 border-t border-border flex-shrink-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Uploaded by</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{photo?.uploader?.full_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Date & Time</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{formatDate(photo?.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Category</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{photo?.task?.category || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">File Size</p>
              <p className="text-sm font-medium text-foreground mt-0.5">{formatSize(photo?.fileSize)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoDetailModal;
